import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  fetchCurrentServiceTime,
  fetchMenuEntries,
  fetchNextServiceTime,
} from '../redux/store/store.actions';
import { customMessageNotification } from '../utility/notification/notification.service';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { getCurrentAtDate, getNextStart } from '../utility/util/util.service';

// Create a context for the service time
const Context = createContext();

export function useServiceTime() {
  const context = useContext(ServiceTimeContext);
  if (!context) {
    throw new Error('useServiceTime must be used within a ServiceTimeProvider');
  }
  return context;
}

const ServiceTimeProviderWrapper = ({ store, children }) => {
  const intl = useIntl();
  const intervalIdRef = useRef(null);
  const [secondsUntilNextStart, setSecondsUntilNextStart] = useState(null);
  const [secondsUntilCurrentEnd, setSecondsUntilCurrentEnd] = useState(null);
  const [nextStartFormatted, setNextStartFormatted] = useState(null);
  const [nextEndFormatted, setNextEndFormatted] = useState(null);
  const serviceTimes = store?.serviceTimes || [];
  const currentServiceTime = getCurrentAtDate(serviceTimes);
  const nextServiceTime = getNextStart(serviceTimes);

  // re-set interval when current and next ServiceTime become available
  useEffect(() => {
    // Clear any existing intervals before setting a new one
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    setSecondsUntilNextStart(null);
    setSecondsUntilCurrentEnd(null);

    if (nextServiceTime) {
      setNextStartFormatted(moment(nextServiceTime.startAt).calendar());
    }

    if (currentServiceTime) {
      setNextEndFormatted(moment(currentServiceTime.endAt).calendar());
    }

    intervalIdRef.current = setInterval(updateTimers, 1000);

    // Clear interval on unmount
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [serviceTimes]);

  const getTimeDifferenceInSeconds = (targetTime) => {
    const currentTime = new Date();
    const diff = new Date(targetTime) - currentTime;
    return Math.floor(diff / 1000);
  };

  const updateTimers = () => {
    if (currentServiceTime) {
      const secondsUntilCurrentEnd = getTimeDifferenceInSeconds(
        currentServiceTime.endAt,
      );
      setSecondsUntilCurrentEnd(secondsUntilCurrentEnd);

      let messageId = null;
      switch (secondsUntilCurrentEnd) {
        case 15 * 60:
          messageId = 'storeClosingIn15min';
          break;
        case 10 * 60:
          messageId = 'storeClosingIn10min';
          break;
        case 5 * 60:
          messageId = 'storeClosingIn5min';
          break;
        case 0:
          messageId = 'storeIsClosed';
          break;
      }

      if (messageId) {
        const message = intl.formatMessage(
          { id: messageId },
          { storeName: store?.name },
        );
        customMessageNotification('warn', message);
      }
    } else if (nextServiceTime) {
      const secondsUntilNextStart = getTimeDifferenceInSeconds(
        nextServiceTime.startAt,
      );
      setSecondsUntilNextStart(secondsUntilNextStart);

      let messageId = null;
      switch (secondsUntilNextStart) {
        case 15 * 60:
          messageId = 'storeOpensIn15min';
          break;
        case 10 * 60:
          messageId = 'storeOpensIn10min';
          break;
        case 5 * 60:
          messageId = 'storeOpensIn5min';
          break;
        case 0:
          messageId = 'storeIsOpened';
          break;
      }

      if (messageId) {
        const message = intl.formatMessage(
          { id: messageId },
          { storeName: store?.name },
        );
        customMessageNotification('warn', message);
      }
    }
  };

  return (
    <ServiceTimeContext.Provider
      value={{
        secondsUntilNextStart,
        secondsUntilCurrentEnd,
        currentServiceTime,
        nextServiceTime,
        nextStartFormatted,
        nextEndFormatted,
      }}
    >
      {children}
    </ServiceTimeContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  me: state.me.me,
  store: state.store.store,
});

export const ServiceTimeContext = Context;
export const ServiceTimeProvider = connect(mapStateToProps, {
  fetchCurrentServiceTime,
  fetchNextServiceTime,
  fetchMenuEntries,
})(ServiceTimeProviderWrapper);
